import { logoutUser } from 'api-client';
import type { AuthAccountWithPassType } from 'api-client/accounts';
import { createUser } from 'api-client/accounts';
import { EMAIL_VERIFICATION_RESULTS, LoginProviderEnum } from 'bc-model';
import ValidatedFormikField from 'components/Utils/Forms/ValidatedFormikField';
import type { FormikProps } from 'formik';
import { Form, Formik } from 'formik';
import type { ReactElement } from 'react';
import { useContext, useEffect, useState } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { includeBasePath } from 'utils/url-utils';
import * as yup from 'yup';
import {
  FormStepEnum,
  SmartLoginActionsContext,
  SmartLoginStateContext,
} from './context/SmartLoginContextProvider';
import PasswordRequirements from './PasswordRequirements';
import { ODSAlert, ODSButton, ODSCheckbox, ODSGrid, ODSInput } from 'bc-ods';
import { useNavigate } from 'react-router-dom';

type FormAuthType = AuthAccountWithPassType & {
  passwordStrength?: number;
  confirmPassword?: string;
};

const messages = defineMessages({
  buttonCreateAccount: {
    id: 'RegistrationForm.buttonCreateAccount',
    defaultMessage: 'Create account',
  },
  buttonBack: {
    id: 'RegistrationForm.buttonBack',
    defaultMessage: 'Back',
  },
  buttonCancel: {
    id: 'RegistrationForm.buttonCancel',
    defaultMessage: 'Cancel',
  },
  passwordLabel: {
    id: 'RegistrationForm.passwordLabel',
    defaultMessage: 'Password',
  },
  emailLabel: {
    id: 'RegistrationForm.emailLabel',
    defaultMessage: 'Email',
  },
  emailPlaceholder: {
    id: 'RegistrationForm.emailPlaceholder',
    defaultMessage: 'e.g. dave@navy.mil',
  },
  usernameLabel: {
    id: 'RegistrationForm.usernameLabel',
    defaultMessage: 'Username',
  },
  usernamePlaceholder: {
    id: 'RegistrationForm.usernamePlaceholder',
    defaultMessage: 'e.g. dbrown',
  },
  confirmPasswordLabel: {
    id: 'RegistrationForm.confirmLabel',
    defaultMessage: 'Confirm password',
  },
  firstNameLabel: {
    id: 'RegistrationForm.firstNameLabel',
    defaultMessage: 'First name',
  },
  firstNamePlaceholder: {
    id: 'RegistrationForm.firstNamePlaceholder',
    defaultMessage: 'e.g. Dave',
  },
  lastNameLabel: {
    id: 'RegistrationForm.lastNameLabel',
    defaultMessage: 'Last name',
  },
  lastNamePlaceholder: {
    id: 'RegistrationForm.lastNamePlaceholder',
    defaultMessage: 'e.g. Brown',
  },
  rankLabel: {
    id: 'RegistrationForm.rankLabel',
    defaultMessage: 'Rank',
  },
  rankPlaceholder: {
    id: 'RegistrationForm.rankPlaceholder',
    defaultMessage: 'e.g. Maj',
  },
  jobTitleLabel: {
    id: 'RegistrationForm.jobTitleLabel',
    defaultMessage: 'Job title',
  },
  jobTitlePlaceholder: {
    id: 'RegistrationForm.jobPlaceholder',
    defaultMessage: 'e.g. J5 Planner',
  },
  organizationLabel: {
    id: 'RegistrationForm.organizationLabel',
    defaultMessage: 'Unit',
  },
  organizationPlaceholder: {
    id: 'RegistrationForm.organizationPlaceholder',
    defaultMessage: 'e.g. CJTF Alpha',
  },
  parentOrganizationLabel: {
    id: 'RegistrationForm.parentOrganizationLabel',
    defaultMessage: 'Parent unit',
  },
  parentOrganizationPlaceholder: {
    id: 'RegistrationForm.parentOrganizationPlaceholder',
    defaultMessage: 'e.g. Sierra Fleet',
  },
  ppLink: {
    id: 'LoginForm.ppLink',
    defaultMessage: 'privacy policy.',
  },
  termsCheck: {
    id: 'LoginForm.termsCheck',
    defaultMessage:
      'Check here to indicate that you have read and agree to ' +
      "Onebrief's {termsLink} and {ppLink}",
  },
  termsLink: {
    id: 'LoginForm.termsLink',
    defaultMessage: 'terms of service',
  },
  scoreWords: {
    id: 'LoginForm.scoreWords',
    defaultMessage:
      '{scoreWord, select, ' +
      'weak {weak} ' +
      'okay {okay} ' +
      'good {good} ' +
      'strong {strong} ' +
      'strongest {strongest} ' +
      'other {too short}}',
  },
  firstNameFeedback: {
    id: 'LoginForm.firstNameFeedback',
    defaultMessage: '{error, select, max {Must be shorter than 60 characters} other {Required}}',
  },
  lastNameFeedback: {
    id: 'LoginForm.lastNameFeedback',
    defaultMessage: '{error, select, max {Must be shorter than 60 characters} other {Required}}',
  },
  usernameFeedback: {
    id: 'LoginForm.usernameFeedback',
    defaultMessage:
      '{error, select,' +
      '  required {Required}' +
      '  min {Must be at least 3 characters long}' +
      '  max {Must be no longer than 30 characters}' +
      '  other {Must only contain letters, numbers, or single dashes in between}' +
      '}',
  },
  emailFeedback: {
    id: 'LoginForm.emailFeedback',
    defaultMessage:
      '{error, select,' +
      '  valid_email {Must be a valid email address}' +
      '  max {Must be no longer than 255 characters}' +
      '  other {Required}' +
      '}',
  },
  rankFeedback: {
    id: 'LoginForm.rankFeedback',
    defaultMessage: '{error, select, max {Must be shorter than 60 characters} other {Required}}',
  },
  jobTitleFeedback: {
    id: 'LoginForm.jobTitleFeedback',
    defaultMessage: '{error, select, max {Must be shorter than 60 characters} other {Required}}',
  },
  organizationFeedback: {
    id: 'LoginForm.organizationFeedback',
    defaultMessage: '{error, select, max {Must be shorter than 60 characters} other {Required}}',
  },
  parentOrganizationFeedback: {
    id: 'LoginForm.parentOrganizationFeedback',
    defaultMessage: '{error, select, max {Must be shorter than 60 characters} other {Required}}',
  },
  feedback: {
    id: 'LoginForm.feedback',
    defaultMessage:
      '{feedback, select, ' +
      '  email {Email already in use.}' +
      '  name {Username already in use.}' +
      '  expired {Session expired.}' +
      '  invalid {Invalid username or password.}' +
      '  unauthorized {Account not authorized.}' +
      '  other {An error occurred. Please review the form and try again.}' +
      '}',
  },
  terms: {
    id: 'LoginForm.terms',
    defaultMessage:
      '{error, select, terms {You must accept the terms of service & privacy policy} other {Required}}',
  },
  termsAccepted: {
    id: 'LoginForm.termsAccepted',
    defaultMessage: 'You must accept the terms of service & privacy policy',
  },
});

const MAX_INPUT_LENGTH = 60; // if you change this, change the appropriate feedback messages above.
// (will) this number is arbitrary, but it's what we've used for given and last name for the past few years.

const isThirdPartyProvider = (provider: LoginProviderEnum): boolean =>
  [LoginProviderEnum.ActiveDirectory, LoginProviderEnum.OIDC, LoginProviderEnum.JWT].includes(
    provider
  );

type ShowAllValidationErrorsOrSubmitParams = {
  e: React.MouseEvent<HTMLButtonElement>;
  requiredFields: string[];
} & Pick<FormikProps<FormAuthType>, 'submitForm' | 'validateForm' | 'setTouched' | 'touched'>;

const showAllValidationErrors = async ({
  e,
  submitForm,
  validateForm,
  setTouched,
  touched,
  requiredFields,
}: ShowAllValidationErrorsOrSubmitParams) => {
  e.preventDefault();

  // touch all required fields to show validation errors if submit is attempted before all required fields are filled
  const newTouched = {
    ...touched,
    ...requiredFields.reduce((acc, field) => ({ ...acc, [field]: true }), {}),
  };

  await setTouched(newTouched, false);
  const newErrors = await validateForm();

  if (Object.keys(newErrors).length === 0) {
    await submitForm();
  }
};

function RegistrationForm(): ReactElement {
  const [showError, setShowError] = useState(false);
  const { profile, provider, matchingId, formError } = useContext(SmartLoginStateContext);
  const { setFormError, setFormStep, goToPreviousStep, formStepHistory } =
    useContext(SmartLoginActionsContext);

  const showBackButton = formStepHistory.length > 1;
  const intl = useIntl();
  const navigate = useNavigate();

  const registrationFieldValidationSchema = yup.object().shape({
    email: yup.string().email('valid_email').required().max(255, 'max'),
    name: yup
      .string()
      .trim()
      .required('required')
      .matches(/^(?!(-|.*-{2,}))[a-zA-Z0-9-]*[a-zA-Z0-9]$/)
      .min(3, 'min')
      .max(30, 'max'),
    firstName: yup.string().trim().max(MAX_INPUT_LENGTH, 'max').required(),
    lastName: yup.string().trim().max(MAX_INPUT_LENGTH, 'max').required(),
    ...(!isThirdPartyProvider(provider) && {
      password: yup
        .string()
        .required('required')
        .min(15, 'min')
        .minLowercase(1, 'lowercase')
        .minUppercase(1, 'uppercase')
        .minNumbers(1, 'digit')
        .minSymbols(1, 'symbol'),
      confirmPassword: yup
        .string()
        .oneOf([yup.ref('password'), ''], 'match')
        .required(),
    }),
    rank: yup.string().trim().max(MAX_INPUT_LENGTH, 'max').required(),
    jobTitle: yup.string().trim().max(MAX_INPUT_LENGTH, 'max').required(),
    organization: yup.string().trim().max(MAX_INPUT_LENGTH, 'max').required(),
    parentOrganization: yup.string().trim().max(MAX_INPUT_LENGTH, 'max').required(),
    termsAccepted: yup.bool().oneOf([true], 'terms').required(),
  });

  const requiredFieldsFromSchema = Object.keys(registrationFieldValidationSchema.fields).filter(
    (field) =>
      registrationFieldValidationSchema.fields[field].tests.some(
        (test) => test.OPTIONS.name === 'required'
      )
  );

  const handleLogout = () => {
    logoutUser();
  };

  const handleSubmitRegistration = async (values: AuthAccountWithPassType) => {
    try {
      setShowError(false);
      const result = await createUser(
        values,
        isThirdPartyProvider(provider)
          ? {
              provider,
              displayName: `${values.firstName!} ${values.lastName!}`,
              username: values.name,
              name: {
                givenName: values.firstName!,
                familyName: values.lastName!,
              },
              id: matchingId !== '' ? matchingId : profile.email ?? '',
            }
          : undefined
      );
      const {
        emailVerified,
        emailVerificationRequired,
        emailVerificationEnabled,
        accountVerifiedStatus,
        userVerificationEnabled,
      } = result.data;

      if (userVerificationEnabled && accountVerifiedStatus !== true) {
        navigate(includeBasePath('/account-status'));
        return;
      }

      if (emailVerificationEnabled && emailVerificationRequired && !emailVerified) {
        navigate(includeBasePath(`/verify-email?result=${EMAIL_VERIFICATION_RESULTS.resent}`));
        return;
      }

      setFormStep(FormStepEnum.Login);
    } catch (error) {
      let msg = 'An unexpected error has occurred. Please try again.';
      const schema = yup.object().shape({
        response: yup.object().shape({
          data: yup.object().shape({
            conflict: yup.string().required(),
          }),
        }),
      });

      if (schema.isValidSync(error)) {
        msg = error.response.data.conflict;
      }

      setFormError(msg);
      setShowError(true);
    }
  };

  useEffect(() => {
    if ((profile as any).autoRegister) {
      (document.querySelector('[data-testid="submit-registration"]') as HTMLElement)?.click();
    }
  }, [profile]);

  return (
    <Formik
      validationSchema={registrationFieldValidationSchema}
      initialValues={{ ...profile } as FormAuthType}
      onSubmit={handleSubmitRegistration}
      validateOnBlur={false}
    >
      {({ isSubmitting, errors, submitForm, validateForm, setTouched, touched }) => (
        <>
          <ODSAlert color="danger" isOpen={showError}>
            <FormattedMessage {...messages.feedback} values={{ feedback: formError }} />
          </ODSAlert>
          <Form data-testid="form-account-creation">
            <ODSGrid columns={1} spacing="lg">
              <ValidatedFormikField
                as={ODSInput}
                name="email"
                id="email"
                label={intl.formatMessage(messages.emailLabel)}
                size="md"
                placeholder={intl.formatMessage(messages.emailPlaceholder)}
                data-testid="email"
                autoFocus
                autoComplete="email"
                disabled={isThirdPartyProvider(provider)}
                errorMessage={intl.formatMessage(messages.emailFeedback, {
                  error: errors.email,
                })}
              />
              <ValidatedFormikField
                name="name"
                id="username"
                label={intl.formatMessage(messages.usernameLabel)}
                size="md"
                placeholder={intl.formatMessage(messages.usernamePlaceholder)}
                data-testid="username"
                autoComplete="username"
                eagerValidation={isThirdPartyProvider(provider)}
                errorMessage={intl.formatMessage(messages.usernameFeedback, {
                  error: errors.name,
                })}
              />
              {!isThirdPartyProvider(provider) && (
                <PasswordRequirements size="md" hideRequirementsOnComplete />
              )}
              <ODSGrid columns={2} spacing="lg">
                <ValidatedFormikField
                  name="firstName"
                  id="firstName"
                  label={intl.formatMessage(messages.firstNameLabel)}
                  size="md"
                  placeholder={intl.formatMessage(messages.firstNamePlaceholder)}
                  disabled={isThirdPartyProvider(provider)}
                  data-testid="first-name"
                  autoComplete="given-name"
                  errorMessage={intl.formatMessage(messages.firstNameFeedback, {
                    error: errors.firstName,
                  })}
                />
                <ValidatedFormikField
                  name="lastName"
                  id="lastName"
                  label={intl.formatMessage(messages.lastNameLabel)}
                  placeholder={intl.formatMessage(messages.lastNamePlaceholder)}
                  size="md"
                  disabled={isThirdPartyProvider(provider)}
                  data-testid="last-name"
                  autoComplete="family-name"
                  errorMessage={intl.formatMessage(messages.lastNameFeedback, {
                    error: errors.lastName,
                  })}
                />
                <ValidatedFormikField
                  name="rank"
                  id="rank"
                  label={intl.formatMessage(messages.rankLabel)}
                  size="md"
                  placeholder={intl.formatMessage(messages.rankPlaceholder)}
                  data-testid="rank"
                  autoComplete="organization-title"
                  errorMessage={intl.formatMessage(messages.rankFeedback, { error: errors.rank })}
                />
                <ValidatedFormikField
                  name="jobTitle"
                  id="jobTitle"
                  label={intl.formatMessage(messages.jobTitleLabel)}
                  size="md"
                  placeholder={intl.formatMessage(messages.jobTitlePlaceholder)}
                  data-testid="job-title"
                  autoComplete="organization-title"
                  errorMessage={intl.formatMessage(messages.jobTitleFeedback, {
                    error: errors.jobTitle,
                  })}
                />
                <ValidatedFormikField
                  name="organization"
                  id="organization"
                  label={intl.formatMessage(messages.organizationLabel)}
                  size="md"
                  placeholder={intl.formatMessage(messages.organizationPlaceholder)}
                  data-testid="organization"
                  autoComplete="organization"
                  errorMessage={intl.formatMessage(messages.organizationFeedback, {
                    error: errors.organization,
                  })}
                />
                <ValidatedFormikField
                  name="parentOrganization"
                  id="parentOrganization"
                  label={intl.formatMessage(messages.parentOrganizationLabel)}
                  size="md"
                  placeholder={intl.formatMessage(messages.parentOrganizationPlaceholder)}
                  data-testid="parent-organization"
                  autoComplete="organization"
                  errorMessage={intl.formatMessage(messages.parentOrganizationFeedback, {
                    error: errors.parentOrganization,
                  })}
                />
              </ODSGrid>
              <ValidatedFormikField
                as={ODSCheckbox}
                name="termsAccepted"
                data-testid="terms"
                className="me-2"
                inline
                smallLabel
                errorMessage={intl.formatMessage(messages.termsAccepted)}
                label={intl.formatMessage(messages.termsCheck, {
                  termsLink: (
                    <a href="/terms" target="_blank" rel="noopener noreferrer">
                      <FormattedMessage {...messages.termsLink} />
                    </a>
                  ),
                  ppLink: (
                    <a href="/privacy" target="_blank" rel="noopener noreferrer">
                      <FormattedMessage {...messages.ppLink} />
                    </a>
                  ),
                })}
              />
              <div className="d-flex gap-2">
                {showBackButton ? (
                  <ODSButton
                    className="flex-shrink-0"
                    size="md"
                    quiet
                    onClick={goToPreviousStep}
                    data-testid="cancel-registration"
                  >
                    <FormattedMessage {...messages.buttonBack} />
                  </ODSButton>
                ) : (
                  <ODSButton
                    className="flex-shrink-0"
                    size="md"
                    quiet
                    onClick={handleLogout}
                    data-testid="cancel-registration"
                  >
                    <FormattedMessage {...messages.buttonCancel} />
                  </ODSButton>
                )}
                <ODSButton
                  color="primary"
                  size="md"
                  className="flex-grow-1"
                  disabled={isSubmitting}
                  type="submit"
                  onClick={async (e) => {
                    if (!(profile as any)?.autoRegister) {
                      await showAllValidationErrors({
                        e,
                        submitForm,
                        validateForm,
                        setTouched,
                        touched,
                        requiredFields: [...requiredFieldsFromSchema, 'termsAccepted'],
                      });
                    }
                  }}
                  data-testid="submit-registration"
                >
                  <FormattedMessage {...messages.buttonCreateAccount} />
                </ODSButton>
              </div>
            </ODSGrid>
          </Form>
        </>
      )}
    </Formik>
  );
}

export default RegistrationForm;
export { isThirdPartyProvider };
